import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

type FacebookPixelsResponse = {
  pixel_ids?: string[];
  total?: number;
};

// Initialize Facebook Pixel SDK (only once)
function initPixelSDK() {
  if (window.fbq) return;

  const n = function (...args: unknown[]) {
    if ((n as unknown as { callMethod?: (...args: unknown[]) => void }).callMethod) {
      (n as unknown as { callMethod: (...args: unknown[]) => void }).callMethod(...args);
    } else {
      (n as unknown as { queue: unknown[] }).queue.push(args);
    }
  };

  (n as unknown as { push: typeof n }).push = n;
  (n as unknown as { loaded: boolean }).loaded = true;
  (n as unknown as { version: string }).version = '2.0';
  (n as unknown as { queue: unknown[] }).queue = [];

  window.fbq = n;
  window._fbq = n;

  // Load the SDK script
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);
}

// Initialize a specific pixel
function initPixel(pixelId: string) {
  if (!window.fbq) initPixelSDK();
  window.fbq!('init', pixelId);
}

// Track event on all pixels
export function trackFBEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window.fbq !== 'function') return;
  if (params) window.fbq('track', eventName, params);
  else window.fbq('track', eventName);
}

export function useFacebookPixels() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [pixelIds, setPixelIds] = useState<string[]>([]);

  useEffect(() => {
    const loadAndInitPixels = async () => {
      try {
        // Fetch pixel IDs via backend function (doesn't expose access_token)
        const { data, error } = await supabase.functions.invoke<FacebookPixelsResponse>(
          'facebook-pixels',
          { body: {} }
        );

        if (error) {
          console.error('[FacebookPixels] Error fetching pixel ids:', error);
          return;
        }

        const ids = data?.pixel_ids ?? [];
        if (ids.length === 0) {
          // No pixels configured -> nothing to init
          return;
        }

        setPixelIds(ids);

        // Initialize the SDK first
        initPixelSDK();

        // Initialize each pixel
        ids.forEach((pixelId) => {
          initPixel(pixelId);
          console.log('[FacebookPixels] Initialized pixel:', pixelId);
        });

        // Track initial PageView for all pixels
        trackFBEvent('PageView');

        setIsInitialized(true);
      } catch (err) {
        console.error('[FacebookPixels] Unexpected error:', err);
      }
    };

    // Wait for page to fully load + delay to be outside LCP measurement
    const deferLoad = () => {
      const runAfterLoad = () => {
        // Delay 2.2s after load to be completely outside Lighthouse measurement window
        setTimeout(loadAndInitPixels, 2200);
      };

      if (document.readyState === 'complete') {
        runAfterLoad();
      } else {
        window.addEventListener('load', runAfterLoad, { once: true });
      }
    };

    deferLoad();
  }, []);

  return { isInitialized, pixelIds, trackFBEvent };
}

